import React, { useState } from "react";
import { useUpdateEffect } from "react-use";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Box, Button, Icon, Text, MetreaTooltip } from "@metrea-ui/v2";

import styles from "./SortableList.module.css";
import { InfoRegular } from "@fluentui/react-icons";

import { mColor, getContrastYIQ } from "src/utils/themeUtils";
import { getValuesByPropertyPath } from "src/utils/dataUtils";
import { debugLog } from "src/utils/debugUtils";

const SortableItem = ({ id, children }) => {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<li ref={setNodeRef} style={style} {...attributes} {...listeners}>
			{children}
		</li>
	);
};

export const SortableList = (props) => {
	const [list, setList] = useState(() => [...props.items].sort((x, y) => x.sort_id - y.sort_id));

	const [chipColors, setChipColors] = useState(getValuesByPropertyPath(list, "color"));
	useUpdateEffect(() => {
		setChipColors(getValuesByPropertyPath(list, "color"));
	}, [list]);

	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		setList((items) => {
			const oldIndex = items.findIndex((i) => i.id === active.id);
			const newIndex = items.findIndex((i) => i.id === over.id);
			return arrayMove(items, oldIndex, newIndex);
		});
	};

	const saveHandler = () => {
		list.map((x, index) => {
			x.sort_id = index + 1;
			x.update_flag = 102;
			return false;
		});

		setList([...list]);
		props.onChange(list);

		debugLog(3, "[SortableList][ACTION] saveHandler", { list: list });
	};

	return (
		<Box display="flex" flexDirection="column" height="100%">
			{/* Keep the scroll region and footer spacing tighter so the tooltip/icon footer
			    alignment still feels balanced inside the dialog after the direct wrapper swap. */}
			<Box flex="1" my="12px" overflowY="auto">
				<DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
					<SortableContext items={list.map((i) => i.id)} strategy={verticalListSortingStrategy}>
						<ul className={styles.sortable_list}>
							{list.map((item, i) => {
								const isCurrentItem = item.id === props.item.id;
								let fontColor = mColor.MidnightBlue;
								if (chipColors[i] && chipColors[i] !== "null") {
									fontColor = getContrastYIQ(chipColors[i])
										? mColor.MidnightBlue
										: mColor.White;
								}
								const rowBackground =
									chipColors[i] && chipColors[i] !== "null" ? chipColors[i] : "#EEEEEE";
								const badgeBackground = getContrastYIQ(rowBackground)
									? "rgba(28, 45, 61, 0.12)"
									: "rgba(255, 255, 255, 0.22)";
								const rowStyle = {
									color: fontColor,
									backgroundColor: rowBackground,
									cursor: "move",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 8,
									padding: 8,
									boxShadow: isCurrentItem ? `0 0 0 2px ${mColor.MidnightBlue}` : "none",
								};

								return (
									<SortableItem key={item.id} id={item.id}>
										<Box style={rowStyle}>
											<Text
												textStyle={isCurrentItem ? "md" : "sm"}
												fontWeight={isCurrentItem ? 600 : 400}
											>
												{item.comp_value}
											</Text>

											{isCurrentItem && (
												<MetreaTooltip
													content="Current Item"
												>
													<Box
														as="span"
														style={{
															display: "inline-flex",
															alignItems: "center",
															padding: "2px 8px",
															backgroundColor: badgeBackground,
															color: fontColor,
															fontSize: "0.75rem",
															fontWeight: 600,
															lineHeight: 1.2,
														}}
													>
														Current
													</Box>
												</MetreaTooltip>
											)}
										</Box>
									</SortableItem>
								);
							})}
						</ul>
					</SortableContext>
				</DndContext>
			</Box>

			<hr />

			{/* 🔽 Fixed footer */}
			<Box pt="12px" pb="0" display="flex" gap="10px" alignItems="center" flexShrink={0}>
				<Button onClick={saveHandler}>Save Item Order</Button>
				<MetreaTooltip
					content="Drag the items to reorder the list."
				>
					<Icon as={InfoRegular} boxSize="1.5rem" color={mColor.StonyBlue} />
				</MetreaTooltip>
			</Box>
		</Box>
	);
	// }
};
